import React from 'react';
import { Button, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import { CopyOutlined, WhatsAppOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  construirMensagemWhatsAppConsolidado,
  corEstadoGrupo,
  formatarValorMonetarioBRL,
  grupoElegivelParaWhatsApp,
  grupoTemAcao,
  grupoTemDocumentoCompartilhavel,
  labelEstadoGrupo,
  normalizarTelefoneWhatsApp,
  resumoConfirmacaoDocumentoGrupo,
} from './cobrancasEventosAcoes';

// Fase D.4b -- card de UMA operação do documento consolidado (BOLETO
// ASAAS de um FinancialChargeGroup). Componente "burro" (mesmo
// princípio de SecaoCobrancasConsolidadas/ConsolidarCobrancasModal):
// ZERO HTTP próprio, ZERO estado de rede -- `grupo` já vem calculado
// (agruparPorGroupId, useMemo em CobrancasEventosAdmin.js) e toda
// mutação é delegada aos callbacks recebidos do pai.
//
// REGRA ARQUITETURAL ABSOLUTA (repetida aqui de propósito, é o ÚNICO
// princípio que este arquivo existe para aplicar): a autorização de
// CADA botão vem EXCLUSIVAMENTE de `grupoTemAcao(grupo, 'ACAO')`, que
// por sua vez só olha `grupo.acoes_permitidas_grupo` (string opaca já
// calculada pelo backend). `grupo.estado_documento_grupo` é usado
// SOMENTE para texto/Tag de apresentação -- nunca para decidir se um
// botão aparece. Os 5 endpoints POST/GET revalidam tudo de novo no
// clique; este componente nunca assume que uma ação vai funcionar.
//
// EMITIR/REEMITIR chamam o MESMO callback (`onEmitir`) -- o backend
// decide, pela história canônica do grupo, se é primeira emissão ou
// reemissão (ver eventoCobrancaAdminService.emitirBoletoGrupo). Só o
// TEXTO do botão/confirmação muda conforme a ação anunciada.
const formatarDataBR = (dataIso) => (dataIso ? dayjs(dataIso).format('DD/MM/YYYY') : '—');

const DocumentoCobrancaConsolidada = ({
  grupo,
  estaAcaoEmAndamento,
  onDissolver,
  onEmitir,
  onVerificarPagamento,
  onCancelar,
  onVerificarCancelamento,
  onCopiarLinhaDigitavel,
  onEnviarWhatsApp,
}) => {
  const groupId = grupo.id_financial_charge_group;
  const primeiro = grupo.componentes[0];
  const quantidade = grupo.componentes.length;
  const eventosResumo = [...new Set(grupo.componentes.map((c) => c.evento_nome))].join(', ');
  const resumo = resumoConfirmacaoDocumentoGrupo(grupo);

  const dissolverEmAndamento = estaAcaoEmAndamento('dissolver-grupo', groupId);
  const emitirEmAndamento = estaAcaoEmAndamento('emitir-grupo', groupId);
  const verificarPagamentoEmAndamento = estaAcaoEmAndamento('verificar-pagamento-grupo', groupId);
  const cancelarEmAndamento = estaAcaoEmAndamento('cancelar-grupo', groupId);
  const verificarCancelamentoEmAndamento = estaAcaoEmAndamento('verificar-cancelamento-grupo', groupId);

  // Fase E.1 -- WhatsApp. `telefoneWhatsApp` ja' normalizado (mesmo
  // padrao de Faturas). Autorizacao NUNCA compara `estado_documento_
  // grupo` como string, e (auditoria final E.1, Secao 4) tambem NUNCA
  // usa `grupoTemAcao(grupo, 'CANCELAR')` -- WhatsApp e cancelamento
  // sao conceitos diferentes, ver docstring de `grupoElegivelParaWhatsApp`.
  // Mensagem usa `valor_total_grupo`/`quantidade_cobrancas_grupo`
  // CANONICOS (nunca a soma/contagem "nesta pagina" usada na
  // confirmacao de EMITIR/REEMITIR acima).
  const telefoneWhatsApp = normalizarTelefoneWhatsApp(grupo.responsavel_telefone);
  const whatsAppElegivel = grupoElegivelParaWhatsApp(grupo, telefoneWhatsApp);
  const mensagemWhatsApp = whatsAppElegivel
    ? construirMensagemWhatsAppConsolidado({
        alunoNome: primeiro.aluno_nome,
        valorFormatado: formatarValorMonetarioBRL(grupo.valor_total_grupo),
        vencimentoFormatado: formatarDataBR(primeiro.data_vencimento),
        quantidade: grupo.quantidade_cobrancas_grupo,
        urlBoleto: grupo.url_boleto_grupo,
        linhaDigitavel: grupo.linha_digitavel_grupo,
      })
    : null;

  // Conteúdo da confirmação de EMITIR/REEMITIR -- aluno/vencimento vêm
  // do componente vigente da página atual (estáveis por Group). A
  // quantidade/valor são EXPLICITAMENTE qualificados como "nesta
  // página" -- o read-model não expõe quantidade/valor canônicos do
  // Group inteiro (auditado em resumoConfirmacaoDocumentoGrupo, ver
  // docstring lá), e um Group pode ter componentes em outras páginas.
  // NUNCA apresentar essa soma parcial como se fosse o total real do
  // documento -- por isso o aviso de autoridade do backend abaixo.
  const detalhesDocumento = (
    <div style={{ maxWidth: 320 }}>
      <div>Aluno: {resumo.alunoNome || '—'}</div>
      <div>Vencimento: {formatarDataBR(resumo.dataVencimento)}</div>
      <div>
        {resumo.quantidadeVisivelNestaPagina} cobrança{resumo.quantidadeVisivelNestaPagina === 1 ? '' : 's'} visível
        {resumo.quantidadeVisivelNestaPagina === 1 ? '' : 'eis'} nesta página ·{' '}
        {formatarValorMonetarioBRL(resumo.valorVisivelNestaPagina)} nesta página
      </div>
      <div style={{ marginTop: 4, color: 'rgba(0,0,0,0.45)' }}>
        O boleto será emitido para todas as cobranças que compõem este grupo. O valor final do documento é
        determinado pelo backend.
      </div>
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '8px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Space size={4} wrap>
            <Typography.Text strong>{primeiro.aluno_nome || '—'}</Typography.Text>
            <Tag color={corEstadoGrupo(grupo.estado_documento_grupo)}>{labelEstadoGrupo(grupo.estado_documento_grupo)}</Tag>
          </Space>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {quantidade} parcela{quantidade === 1 ? '' : 's'} visível{quantidade === 1 ? '' : 'eis'} nesta página ·
              Vencimento {formatarDataBR(primeiro.data_vencimento)} · {formatarValorMonetarioBRL(resumo.valorVisivelNestaPagina)}{' '}
              nesta página · {eventosResumo}
            </Typography.Text>
          </div>
        </div>

        <Space size={8} wrap>
          {grupoTemAcao(grupo, 'DISSOLVER') && (
            <Popconfirm
              title="Desfazer consolidação?"
              description="Estas cobranças voltarão a ficar separadas. Nenhum boleto foi emitido."
              onConfirm={() => onDissolver(grupo)}
              okText="Desfazer"
              cancelText="Voltar"
            >
              <Button size="small" danger loading={dissolverEmAndamento} disabled={dissolverEmAndamento}>
                Desfazer consolidação
              </Button>
            </Popconfirm>
          )}

          {grupoTemAcao(grupo, 'EMITIR') && (
            <Popconfirm
              title="Emitir boleto consolidado?"
              description={
                <div>
                  <div>Será gerado um único boleto consolidado para todas as cobranças deste grupo.</div>
                  {detalhesDocumento}
                  <div style={{ marginTop: 4 }}>Após a emissão, a composição deste grupo não poderá ser alterada.</div>
                </div>
              }
              onConfirm={() => onEmitir(grupo)}
              okText="Emitir"
              cancelText="Voltar"
            >
              <Button size="small" type="primary" loading={emitirEmAndamento} disabled={emitirEmAndamento}>
                Emitir boleto consolidado
              </Button>
            </Popconfirm>
          )}

          {grupoTemAcao(grupo, 'REEMITIR') && (
            <Popconfirm
              title="Reemitir boleto?"
              description={
                <div>
                  <div>O boleto anterior foi cancelado. Será gerado um novo documento para o mesmo grupo de cobranças.</div>
                  {detalhesDocumento}
                </div>
              }
              onConfirm={() => onEmitir(grupo)}
              okText="Reemitir"
              cancelText="Voltar"
            >
              <Button size="small" type="primary" loading={emitirEmAndamento} disabled={emitirEmAndamento}>
                Reemitir boleto
              </Button>
            </Popconfirm>
          )}

          {grupoTemAcao(grupo, 'VERIFICAR_PAGAMENTO') && (
            <Tooltip title="Consulta a situação no provedor. Não cria uma nova cobrança.">
              <Button
                size="small"
                loading={verificarPagamentoEmAndamento}
                disabled={verificarPagamentoEmAndamento}
                onClick={() => onVerificarPagamento(grupo)}
              >
                Verificar pagamento
              </Button>
            </Tooltip>
          )}

          {grupoTemAcao(grupo, 'CANCELAR') && (
            <Popconfirm
              title="Cancelar boleto consolidado?"
              description={
                <div style={{ maxWidth: 320 }}>
                  <div>
                    Esta ação cancela o boleto consolidado no provedor. As cobranças que compõem o grupo NÃO serão
                    canceladas e continuarão em aberto/vencidas.
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Se o cancelamento for confirmado, o mesmo grupo poderá ser elegível para reemissão.
                  </div>
                </div>
              }
              onConfirm={() => onCancelar(grupo)}
              okText="Cancelar boleto"
              cancelText="Voltar"
            >
              <Button size="small" danger loading={cancelarEmAndamento} disabled={cancelarEmAndamento}>
                Cancelar boleto
              </Button>
            </Popconfirm>
          )}

          {grupoTemAcao(grupo, 'VERIFICAR_CANCELAMENTO') && (
            <Button
              size="small"
              loading={verificarCancelamentoEmAndamento}
              disabled={verificarCancelamentoEmAndamento}
              onClick={() => onVerificarCancelamento(grupo)}
            >
              Verificar cancelamento
            </Button>
          )}

          {/* Documento vigente com identificador util (mesmo criterio de
              `grupoElegivelParaWhatsApp`, sem o telefone) mas sem
              telefone -- botao fica desabilitado com tooltip explicando
              o motivo, em vez de simplesmente desaparecer (padrao ja
              usado por Reenviar/Excluir em FaturasList.js: Tooltip +
              Button disabled). Sem documento vigente/identificador, nao
              mostra nada (mesmo padrao dos demais botoes deste card). */}
          {grupoTemDocumentoCompartilhavel(grupo) && !telefoneWhatsApp && (
            <Tooltip title="Responsável financeiro sem telefone cadastrado.">
              <span>
                <Button size="small" icon={<WhatsAppOutlined />} disabled>
                  Enviar por WhatsApp
                </Button>
              </span>
            </Tooltip>
          )}
          {whatsAppElegivel && (
            <Button
              size="small"
              icon={<WhatsAppOutlined />}
              onClick={() => onEnviarWhatsApp({ telefone: telefoneWhatsApp, mensagem: mensagemWhatsApp })}
            >
              Enviar por WhatsApp
            </Button>
          )}
        </Space>
      </div>

      {(grupo.linha_digitavel_grupo || grupo.codigo_barras_grupo) && (
        <div style={{ fontSize: 12 }}>
          {grupo.linha_digitavel_grupo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Typography.Text type="secondary" style={{ wordBreak: 'break-all' }}>
                Linha digitável: {grupo.linha_digitavel_grupo}
              </Typography.Text>
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => onCopiarLinhaDigitavel(grupo.linha_digitavel_grupo)}
              >
                Copiar linha digitável
              </Button>
            </div>
          )}
          {grupo.codigo_barras_grupo && (
            <div>
              <Typography.Text type="secondary" style={{ wordBreak: 'break-all' }}>
                Código de barras: {grupo.codigo_barras_grupo}
              </Typography.Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentoCobrancaConsolidada;
